// frontend/src/components/mobile/MobileBottomNav.jsx
// Project:      Vision Systems / Vision ANPR
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-09-23
// Updated:      2026-09-23 (radial alignment and design-preview actions)
//
// Description:  Persistent PWA bottom navigation with a closed-by-default,
//               theme-aware quick-action ring. The component owns presentation
//               and interaction only; the Vision application shell owns routing,
//               RBAC/entitlement filtering, and existing creation workflows.
//               Navigation selection closes the quick-action ring before the
//               shell changes route or opens another global surface.
//               Function parameter defaults are used because React 19 no longer
//               applies defaultProps to function components reliably.

import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import MobileQuickActions from './MobileQuickActions';
import './mobileBottomNav.css';

function NavigationIcon({ icon }) {
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      'aria-hidden': true,
      focusable: false,
    });
  }

  if (typeof icon === 'function') {
    const IconComponent = icon;
    return <IconComponent aria-hidden="true" focusable="false" />;
  }

  return <FontAwesomeIcon icon={icon} aria-hidden="true" fixedWidth />;
}

NavigationIcon.propTypes = {
  icon: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func,
    PropTypes.element,
    PropTypes.array,
  ]).isRequired,
};

function NavigationItem({ activeTab = null, item, onNavigate }) {
  const isActive = activeTab === item.id;

  return (
    <button
      type="button"
      className={`vision-mobile-nav__item${isActive ? ' is-active' : ''}`}
      onClick={() => onNavigate(item.id, item)}
      aria-current={isActive ? 'page' : undefined}
      aria-label={item.label}
      disabled={item.disabled === true}
    >
      <span className="vision-mobile-nav__item-icon">
        <NavigationIcon icon={item.icon} />
      </span>
      <span className="vision-mobile-nav__item-label">{item.label}</span>
    </button>
  );
}

NavigationItem.propTypes = {
  activeTab: PropTypes.string,
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.func,
      PropTypes.element,
      PropTypes.array,
    ]).isRequired,
    disabled: PropTypes.bool,
  }).isRequired,
  onNavigate: PropTypes.func.isRequired,
};

export default function MobileBottomNav({
  activeTab = null,
  ariaLabel = 'Mobile navigation',
  className = '',
  navigationItems = [],
  onAction,
  onNavigate,
  quickActions = [],
}) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleButtonRef = useRef(null);
  const quickActionsId = useId();
  const visibleNavigationItems = navigationItems.slice(0, 4);
  const leftItems = visibleNavigationItems.slice(0, 2);
  const rightItems = visibleNavigationItems.slice(2, 4);
  const hasQuickActions = quickActions.length > 0;

  useEffect(() => {
    setIsOpen(false);
  }, [activeTab]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        toggleButtonRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const closeQuickActions = () => setIsOpen(false);
  const handleNavigate = (navigationId, item) => {
    closeQuickActions();
    onNavigate(navigationId, item);
  };

  return (
    <div
      className={`vision-mobile-nav${isOpen ? ' is-open' : ''}${
        className ? ` ${className}` : ''
      }`}
      data-mobile-navigation="true"
    >
      {isOpen && (
        <button
          type="button"
          className="vision-mobile-nav__scrim"
          onClick={closeQuickActions}
          tabIndex={-1}
          aria-label="Close quick actions"
        />
      )}

      <nav className="vision-mobile-nav__bar" aria-label={ariaLabel}>
        <div className="vision-mobile-nav__side vision-mobile-nav__side--left">
          {leftItems.map((item) => (
            <NavigationItem
              key={item.id}
              activeTab={activeTab}
              item={item}
              onNavigate={handleNavigate}
            />
          ))}
        </div>

        <div className="vision-mobile-nav__action-hub">
          <div
            id={quickActionsId}
            className="vision-mobile-nav__quick-actions-host"
          >
            {isOpen && hasQuickActions && (
              <MobileQuickActions
                actions={quickActions}
                onAction={onAction}
                onRequestClose={closeQuickActions}
              />
            )}
          </div>

          <button
            ref={toggleButtonRef}
            type="button"
            className="vision-mobile-nav__toggle"
            onClick={() => setIsOpen((currentValue) => !currentValue)}
            aria-controls={quickActionsId}
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Close quick actions' : 'Open quick actions'}
            disabled={!hasQuickActions}
          >
            <FontAwesomeIcon
              icon={isOpen ? faTimes : faPlus}
              aria-hidden="true"
            />
          </button>
          <span className="vision-mobile-nav__toggle-label">
            {isOpen ? 'Close' : 'Actions'}
          </span>
        </div>

        <div className="vision-mobile-nav__side vision-mobile-nav__side--right">
          {rightItems.map((item) => (
            <NavigationItem
              key={item.id}
              activeTab={activeTab}
              item={item}
              onNavigate={handleNavigate}
            />
          ))}
        </div>
      </nav>
    </div>
  );
}

MobileBottomNav.propTypes = {
  activeTab: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  navigationItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func,
        PropTypes.element,
        PropTypes.array,
      ]).isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  onAction: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  quickActions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func,
        PropTypes.element,
        PropTypes.array,
      ]).isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
};
